import React, { useContext } from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Link,
  TextField,
  Typography
} from "@mui/material";
import { motion } from "framer-motion";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import {
  KivuI18nContext,
  EMAIL_REGEX,
  localizedPath,
  useKivunovaTranslation,
  useToggleVisibility
} from "@kivunova/kivufrontendcommon";
import useLogin from "../../hooks/api/useLogin";
import {
  PAGE_ROUTE_FORGOT_PASSWORD,
  PAGE_ROUTE_REGISTRATION
} from "@kivunova/kivufrontendcommon";
import AppButton from "../common/AppButton";
import {
  authFormInputSx,
  authFormItemMotion,
  authFormStagger
} from "../common/constants/authFormStyles";

import type { LoginFormData } from "./types/login.types";
import { linkButtonSx } from "../common/constants/buttonStyles";
import { getPrivacyPolicyUrl } from "../../Utils/kivunovaWebsite";
import PasswordVisibilityAdornment from "../common/PasswordVisibilityAdornment/PasswordVisibilityAdornment";

export interface LoginFormProps {
  setLoginInfo: (loginInfo: string) => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ setLoginInfo }) => {
  const navigate = useNavigate();
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext);
  const loginMutation = useLogin();
  const isPending = loginMutation.isPending;
  const { isVisible: isPasswordVisible, toggle: togglePasswordVisibility } =
    useToggleVisibility(false);

  const schema = yup.object<LoginFormData>().shape({
    email: yup
      .string()
      .required(t("email-invalid-error", "Email is required"))
      .matches(EMAIL_REGEX, t("email-empty-error", "Enter a valid email")),
    password: yup
      .string()
      .required(t("password-empty-password", "Password is required"))
      .min(
        8,
        t("password-invalid-error", "Password should be at least 8 characters")
      ),
    remember: yup.boolean().default(false)
  });

  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<LoginFormData>({
    resolver: yupResolver(schema),
    defaultValues: { email: "", password: "", remember: false }
  });

  const handleClick = async (loginData: LoginFormData) => {
    try {
      setLoginInfo(loginData.email);
      await loginMutation.mutateAsync({
        email: loginData.email,
        password: loginData.password
      });
    } catch (e) {
      console.error("failed to login", e);
    }
  };

  const handleForgotPassword = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(localizedPath(PAGE_ROUTE_FORGOT_PASSWORD, language, defaultLang));
  };

  const handleSignUp = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(localizedPath(PAGE_ROUTE_REGISTRATION, language, defaultLang));
  };

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      sx={{
        flex: 1,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: { xs: "flex-start", md: "center" },
        alignItems: "center",
        p: { xs: 2, sm: 3, md: 4 }
      }}
      data-testid="login-right-panel"
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 420,
          display: "flex",
          flexDirection: "column",
          justifyContent: { xs: "flex-start", md: "center" }
        }}
      >
        <motion.div
          variants={authFormStagger}
          initial="initial"
          animate="animate"
        >
          <motion.div variants={authFormItemMotion}>
            <Typography
              variant="h5"
              fontWeight="bold"
              textAlign="center"
              sx={{ mb: 1 }}
            >
              {t("login-text", "Login")}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ mb: { xs: 2, sm: 3 } }}
            >
              {t(
                "login-welcome-back-text",
                "Welcome back! Please enter your details."
              )}
            </Typography>
          </motion.div>

          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit(handleClick)(e);
            }}
            noValidate
            className="form-stack"
            data-testid="login-form"
            sx={{ maxWidth: 420, mx: "auto" }}
          >
            <motion.div variants={authFormItemMotion}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    inputProps={{ "data-testid": "login-email" }}
                    label={t("email-input-label", "Email")}
                    placeholder={t(
                      "email-input-placeholder",
                      "Enter your email"
                    )}
                    type="email"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    disabled={isPending}
                    sx={authFormInputSx}
                  />
                )}
              />
            </motion.div>

            <motion.div variants={authFormItemMotion}>
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    inputProps={{ "data-testid": "login-password" }}
                    label={t("password-input-label", "Password")}
                    type={isPasswordVisible ? "text" : "password"}
                    placeholder={t(
                      "password-input-placeholder",
                      "Enter your password"
                    )}
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    disabled={isPending}
                    sx={authFormInputSx}
                    InputProps={{
                      endAdornment: (
                        <PasswordVisibilityAdornment
                          isVisible={isPasswordVisible}
                          onToggle={togglePasswordVisibility}
                          disabled={isPending}
                        />
                      )
                    }}
                  />
                )}
              />
            </motion.div>

            {/* ── Remember me + forgot password ── */}
            <motion.div variants={authFormItemMotion}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "space-between",
                  alignItems: { xs: "flex-start", sm: "center" },
                  gap: { xs: 1, sm: 0 }
                }}
              >
                <Controller
                  name="remember"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value}
                          color="primary"
                          sx={{ "& .MuiSvgIcon-root": { borderRadius: 1 } }}
                        />
                      }
                      label={t("remember-me-input-label", "Remember me")}
                    />
                  )}
                />

                <Link
                  component="a"
                  href="#"
                  underline="hover"
                  sx={{ fontSize: "0.9rem", color: "primary.main" }}
                  onClick={handleForgotPassword}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleForgotPassword(e);
                    }
                  }}
                >
                  {t("forgot-password-link-text", "Forgot password?")}
                </Link>
              </Box>
            </motion.div>

            <motion.div variants={authFormItemMotion}>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: { xs: 1.5, sm: 2 },
                  width: "100%"
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: "right" }}
                >
                  {t(
                    "by-clicking-agree-terms",
                    "By clicking, you agree to our "
                  )}
                  <Link
                    href={getPrivacyPolicyUrl(language || defaultLang || "en")}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="always"
                    sx={linkButtonSx}
                  >
                    {t(
                      "terms-and-privacy-policy-link",
                      "Terms and Privacy Policy"
                    )}
                  </Link>
                </Typography>
                <AppButton
                  type="submit"
                  variant="primary"
                  disabled={isPending}
                  data-testid="login-submit"
                  className="touch-target"
                  sx={{
                    py: 1.25,
                    px: 3,
                    fontSize: "1rem",
                    borderRadius: 2,
                    minHeight: { xs: 44, md: "auto" },
                    flexShrink: 0
                  }}
                >
                  {t("login-button-text", "Log in")}
                </AppButton>
              </Box>
            </motion.div>

            <motion.div variants={authFormItemMotion}>
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Typography variant="body2">
                  {t("no-account-question-text", "Don't have an account?")}{" "}
                  <Link
                    component="a"
                    href="#"
                    underline="hover"
                    sx={linkButtonSx}
                    onClick={handleSignUp}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        handleSignUp(e);
                      }
                    }}
                  >
                    {t("sinput-link-text", "Sign up")}
                  </Link>
                </Typography>
              </Box>
            </motion.div>
          </Box>
        </motion.div>
      </Box>
    </Box>
  );
};

export default LoginForm;
